import { Vector2, Vector3, Matrix4, Quaternion } from 'three';
import { Cartographic, Ellipsoid, } from '@skand/three-js-renderer/bin/private/math';
import proj4 from 'proj4';
/**
 * Convert a camera space coordinate to ECEF world space
 *
 * @param cameraSpace          Position in camera space
 * @param extrinsic            Extrinsic matrix of the camera
 * @param spatialReferenceProj Spatial reference projection string
 */
function cameraSpaceToCartesian(cameraSpace, extrinsic, spatialReferenceProj) {
    // Compute position in the provided reference system
    const referenceSystemSpace = cameraSpace.clone().applyMatrix4(extrinsic);
    // Convert to ECEF
    if (spatialReferenceProj.search('LOCAL_CS') >= 0) {
        return referenceSystemSpace;
    }
    else {
        try {
            const ecefSpace = proj4(spatialReferenceProj, 'EPSG:4326').forward(referenceSystemSpace);
            return Ellipsoid.WGS_84.cartesianForCartographic(new Cartographic((ecefSpace.x * Math.PI) / 180, (ecefSpace.y * Math.PI) / 180, ecefSpace.z));
        }
        catch (error) {
            throw new Error(`Invalid Projection Error: ${error}`);
        }
    }
}
/**
 * Convert an image space coordinate to camera space
 *
 * This applies correction for tangential and radial distortions
 *
 * @param imagePoint Position in image space
 * @param camera     Camera parameters
 */
function imageSpaceToCamera(imagePoint, camera) {
    const { sensorSize, imageSize, focalLength, principalPoint, distortion } = camera;
    const { k1, k2, k3, p1, p2 } = distortion;
    const pixelSize = sensorSize / Math.max(imageSize.x, imageSize.y);
    const norm = focalLength / pixelSize;
    const d0 = imagePoint.clone().sub(principalPoint).divideScalar(norm);
    let d = d0.clone();
    const t = new Vector2();
    const iters = 7;
    for (let i = 0; i < iters; i++) {
        const r2 = d.lengthSq();
        // Brown-Conrady equations
        const df = 1 / (1 + ((k3 * r2 + k2) * r2 + k1) * r2);
        t.x = 2 * p1 * d.x * d.y + p2 * (r2 + 2 * d.x * d.x);
        t.y = 2 * p2 * d.x * d.y + p1 * (r2 + 2 * d.y * d.y);
        // Iterative update
        d = d0.clone().sub(t).multiplyScalar(df);
    }
    return new Vector3(d.x * norm, d.y * norm, 1);
}
/**
 * Calcualte the position and rotation of the camera model in ECEF facing
 * a deprojected point from the image
 *
 * @param imagePoint        Point on the image to rotate the camera towards
 * @param extrinsicPosition Extrinsic position of the camera
 * @param extrinsicRotation Extrinsic rotation of the camera
 * @param extrinsicScale    Extrinsic scale of the camera
 * @param camera            Camera parameters
 */
function calculateCameraECEFTransform(imagePoint, extrinsicPosition, extrinsicRotation, extrinsicScale, camera) {
    const { spatialReferenceProj } = camera;
    const cameraSpace = imageSpaceToCamera(imagePoint, camera);
    const extrinsic = new Matrix4().compose(extrinsicPosition, extrinsicRotation, extrinsicScale);
    const target = cameraSpaceToCartesian(cameraSpace, extrinsic, spatialReferenceProj);
    const eye = cameraSpaceToCartesian(new Vector3(0, 0, 0), extrinsic, spatialReferenceProj);
    const up = cameraSpaceToCartesian(new Vector3(0, -1, 0), extrinsic, spatialReferenceProj).sub(eye);
    const M = new Matrix4().lookAt(eye, target, up);
    return {
        position: eye,
        rotation: new Quaternion().setFromRotationMatrix(M),
    };
}
export { cameraSpaceToCartesian, imageSpaceToCamera, calculateCameraECEFTransform, };
